import React from 'react';
import type { EmailExclusionJob } from '../types';
import { Icon, Tooltip } from 'antd';
import { ProgressBar, Step } from 'react-step-progress-bar';
import { getEmailExclusionJobProgressAndText } from '../utils';

type Props = {
  job: EmailExclusionJob,
};

const steps = [
  {
    text: 'Upload',
    completedText: 'Uploaded',
    icon: 'cloud-upload',
    tooltip: 'File uploaded to server',
  },
  {
    text: (job: EmailExclusionJob) => {
      if (job.status === 'pending') {
        return 'Waiting for import';
      }
      if (job.status === 'importing') {
        const numOfExclusionFiles = job.exclusionFiles.length,
          numOfProcessedExclusionFiles = job.exclusionFiles.filter(
            file => file.status === 'imported' || file.status === 'failed',
          ).length,
          numOfCampaignFiles = job.campaignFiles.length,
          numOfProcessedCampaignFiles = job.campaignFiles.filter(
            file => file.status === 'imported' || file.status === 'failed',
          ).length;
        const numOfFiles = numOfExclusionFiles + numOfCampaignFiles,
          numOfProcessedFiles =
            numOfProcessedExclusionFiles + numOfProcessedCampaignFiles;
        return `Importing (${numOfProcessedFiles}/${numOfFiles} files imported)`;
      }
      if (job.status === 'failed') {
        return 'Importing failed';
      }
      return `Imported`;
    },
    completedText: 'Imported',
    icon: 'security-scan',
    tooltip: 'File will be validated for errors',
    error: errorType => errorType === 'importing',
  },
  {
    text: (job: EmailExclusionJob) => {
      if (['imported', 'pending', 'importing'].includes(job.status)) {
        return 'Waiting for processing';
      }
      if (job.status === 'processing') {
        const countProcessed = job.campaignFiles.filter(
          file => file.status === 'processed',
        ).length;
        return `Processing (${countProcessed}/${job.campaignFiles.length} campaign files processed)`;
      }
      if (job.status === 'failed') {
        return 'Processing failed';
      }
      return `Processed`;
    },
    completedText: 'Processed',
    icon: 'cluster',
    tooltip:
      'File will be processed and split into multiple files based on the planning CSV',
    error: errorType => errorType === 'processing',
  },
  {
    text: 'Finalizing',
    completedText: 'Ready',
    icon: 'carry-out',
    tooltip:
      'Multiple files will be compressed into one and ready for download',
  },
];

export default function ExclusionJobDetailsStatusBar(props: Props) {
  const { progress, errorType } = getEmailExclusionJobProgressAndText(
    props.job,
  );
  return (
    <div className="w-full space-y-4">
      <b>Status</b>
      <div style={{ padding: '20px 70px 60px' }}>
        <ProgressBar filledBackground="#84cc16" height={4} percent={progress}>
          {steps.map((step, index) => {
            return (
              <Step key={index}>
                {stepProps => (
                  <RenderStep
                    {...step}
                    {...stepProps}
                    error={step.error && step.error(errorType)}
                    job={props.job}
                    progress={progress}
                  />
                )}
              </Step>
            );
          })}
        </ProgressBar>
      </div>
    </div>
  );
}

type StepProps = {
  accomplished: boolean,
  index: number,
  progress: number,
  transition: string,
  icon: string,
  error?: boolean,
  text: string,
  completedText: string,
  tooltip: string,
  job: EmailExclusionJob,
};

function RenderStep(props: StepProps) {
  const stepProgress = (100 / 3) * props.index;
  const progressDelta = stepProgress - props.progress;
  const inProgress =
    progressDelta > 0 && progressDelta < 100 / 3 && !props.error;
  let fontColor, borderColor, textColor;
  if (props.accomplished) {
    fontColor = '#fff';
    borderColor = '#84cc16';
    textColor = '#84cc16';
  } else if (inProgress) {
    fontColor = '#eab308';
    borderColor = '#eab308';
  } else {
    fontColor = '#ccc';
    borderColor = '#ccc';
  }
  if (props.error) {
    fontColor = '#dc2626';
    borderColor = '#dc2626';
  }
  return (
    <div
      className="flex flex-col items-center"
      style={{ backgroundColor: '#fff', padding: '0 10px' }}
    >
      <Tooltip title={props.tooltip}>
        <Icon
          className={`${inProgress ? 'animation-pulse' : ''}`}
          style={{ fontSize: 60, color: borderColor }}
          type={props.icon}
        />
      </Tooltip>
      <b
        style={{
          fontSize: 13,
          color: textColor || fontColor,
          position: 'absolute',
          top: 70,
          whiteSpace: 'nowrap',
        }}
      >
        {props.accomplished
          ? props.completedText
          : typeof props.text === 'function'
          ? props.text(props.job)
          : props.text}
      </b>
    </div>
  );
}
